import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type AssignResourcesHomeSiteRequest,
  type CreateResourceRequest,
  type LinkResourceMobileUserRequest,
  type Resource,
  type ResourceCertification,
  type SetResourceCertificationRequest,
  type UpdateResourceRequest,
} from '../client';

/**
 * Stable query-key factory for resources. Account scoping is implicit via
 * the active token; explicit `organisationId` segment so switching orgs
 * in the AccountSwitcher always refetches.
 */
export const resourceKeys = {
  all: ['directory', 'resources'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'resources', 'org', organisationId] as const,
  byId: (id: string) => ['directory', 'resources', 'id', id] as const,
};

/**
 * Fetch every resource in an organisation, certifications eagerly
 * included. The BE sorts by type (drivers first) then name; client-side
 * filters / sorts further.
 *
 * Pass `{ enabled: false }` to suppress the call (e.g. during the auth
 * bootstrap before a token exists).
 */
export function useResources(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<Resource[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Resource[], Error>({
    queryKey: organisationId ? resourceKeys.byOrganisation(organisationId) : resourceKeys.all,
    queryFn: () => directoryApi.getResources(organisationId!),
    enabled: !!organisationId,
    staleTime: 60 * 1000, // 1 minute — resources change rarely; certs slightly more.
    ...options,
  });
}

/**
 * Fetch a single resource (with certifications). Mainly used by the
 * detail / edit page — the list query covers most other reads.
 */
export function useResource(id: string | null) {
  return useQuery<Resource, Error>({
    queryKey: id ? resourceKeys.byId(id) : resourceKeys.all,
    queryFn: () => directoryApi.getResource(id!),
    enabled: !!id,
  });
}

/**
 * Create a resource. Invalidates the org list query so the new row
 * appears immediately. Cert rows are added separately via
 * `useSetResourceCertification` after the resource lands.
 */
export function useCreateResource() {
  const queryClient = useQueryClient();
  return useMutation<Resource, Error, CreateResourceRequest>({
    mutationFn: (body) => directoryApi.createResource(body),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(resource.organisationId),
      });
    },
  });
}

/**
 * Update a resource. Invalidates the parent org's list (in case status /
 * compliance shifted the row's position) and the single-resource cache.
 */
export function useUpdateResource() {
  const queryClient = useQueryClient();
  return useMutation<Resource, Error, UpdateResourceRequest>({
    mutationFn: (body) => directoryApi.updateResource(body),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(resource.organisationId),
      });
      queryClient.invalidateQueries({ queryKey: resourceKeys.byId(resource.id) });
    },
  });
}

/**
 * Assign (or clear) the home depot on many resources at once. Resolves to the
 * number of rows changed. One request for the whole batch, then a single
 * org-wide invalidation — the alternative (looping `useUpdateResource`) would
 * fire hundreds of requests and re-send every field of every resource.
 */
/**
 * Link a driver to the phone identity they sign in with (or clear it with
 * `mobileUserId: null`). Invalidates the org's resources so the Drivers grid
 * picks the change up, and the mobile-user list, whose rows are the other side
 * of the same link.
 */
export function useLinkResourceMobileUser() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, LinkResourceMobileUserRequest>({
    mutationKey: [...resourceKeys.all, 'mobile-user', 'link'],
    mutationFn: (body) => directoryApi.linkResourceMobileUser(body),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(variables.organisationId),
      });
      queryClient.invalidateQueries({ queryKey: ['directory', 'mobile-users'] });
    },
  });
}

export function useAssignResourcesHomeSite() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, AssignResourcesHomeSiteRequest>({
    mutationKey: [...resourceKeys.all, 'homeSite', 'assign'],
    mutationFn: (body) => directoryApi.assignResourcesHomeSite(body),
    onSuccess: (_changed, variables) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(variables.organisationId),
      });
      // Roster reads the depot off the resource now, so refresh it too.
      queryClient.invalidateQueries({ queryKey: ['directory', 'roster'] });
    },
  });
}

/**
 * Soft-delete a resource. Caller passes `organisationId` so the right
 * list query gets invalidated (the deleted resource's payload isn't
 * available post-delete, so we can't read it back).
 */
export function useDeleteResource() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: string; organisationId: string }>({
    mutationFn: ({ id }) => directoryApi.deleteResource(id),
    onSuccess: (_, { organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(organisationId),
      });
    },
  });
}

/**
 * Upsert a single cert on a resource. The BE recomputes the resource's
 * compliance roll-up and returns the cert; we invalidate the parent
 * org's list so the dot colour + nextExpiryDate update.
 */
export function useSetResourceCertification() {
  const queryClient = useQueryClient();
  return useMutation<
    ResourceCertification,
    Error,
    { body: SetResourceCertificationRequest; organisationId: string }
  >({
    mutationFn: ({ body }) => directoryApi.setResourceCertification(body),
    onSuccess: (_, { organisationId, body }) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(organisationId),
      });
      queryClient.invalidateQueries({ queryKey: resourceKeys.byId(body.resourceId) });
    },
  });
}

/**
 * Soft-delete a single cert off a resource. Same invalidation pattern
 * as upsert — the resource's compliance roll-up may have changed.
 */
export function useDeleteResourceCertification() {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { resourceId: string; certificationId: string; organisationId: string }
  >({
    mutationFn: ({ resourceId, certificationId }) =>
      directoryApi.deleteResourceCertification(resourceId, certificationId),
    onSuccess: (_, { resourceId, organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(organisationId),
      });
      queryClient.invalidateQueries({ queryKey: resourceKeys.byId(resourceId) });
    },
  });
}

/**
 * Upload (or replace) a cert's attachment file. The BE bumps the
 * version + returns the cert with a fresh SAS URL the FE can render
 * immediately.
 */
export function useUploadResourceCertificationAttachment() {
  const queryClient = useQueryClient();
  return useMutation<
    ResourceCertification,
    Error,
    { resourceId: string; certificationId: string; file: File; organisationId: string }
  >({
    mutationFn: ({ resourceId, certificationId, file }) =>
      directoryApi.uploadResourceCertificationAttachment(resourceId, certificationId, file),
    onSuccess: (_, { resourceId, organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(organisationId),
      });
      queryClient.invalidateQueries({ queryKey: resourceKeys.byId(resourceId) });
    },
  });
}

/**
 * Clear a cert's attachment metadata. Idempotent BE-side; we still
 * invalidate so the SAS URL disappears from the cached cert row.
 */
export function useDeleteResourceCertificationAttachment() {
  const queryClient = useQueryClient();
  return useMutation<
    ResourceCertification,
    Error,
    { resourceId: string; certificationId: string; organisationId: string }
  >({
    mutationFn: ({ resourceId, certificationId }) =>
      directoryApi.deleteResourceCertificationAttachment(resourceId, certificationId),
    onSuccess: (_, { resourceId, organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.byOrganisation(organisationId),
      });
      queryClient.invalidateQueries({ queryKey: resourceKeys.byId(resourceId) });
    },
  });
}
